import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/auth';
import { useMessaging } from '../contexts/MessagingContext';
import { api } from '../lib/api';
import { ConversationList } from '../components/ConversationList';
import { ChatPanel } from '../components/ChatPanel';

export function MessagingPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    conversations,
    activeConversationId,
    messages,
    messagesLoading,
    hasMoreMessages,
    sendMessage,
    markAsRead,
    loadMoreMessages,
    setActiveConversation,
    loadConversations,
    openConversation,
    conversationsLoading,
  } = useMessaging();

  const [showMobileChat, setShowMobileChat] = useState(false);

  // Load conversations on mount: try family first, fall back to care home
  useEffect(() => {
    async function load() {
      try {
        const families = await api.getFamilies();
        if (families.length > 0) {
          await loadConversations({ familyId: families[0].id });
          return;
        }
      } catch {
        // not a family user
      }
      try {
        const homes = await api.getCareHomes();
        const homeId = searchParams.get('careHomeId') || homes[0]?.id;
        if (homeId) await loadConversations({ careHomeId: homeId });
      } catch {
        // ignore
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open specific conversation from URL params
  useEffect(() => {
    const dependentId = searchParams.get('dependentId');
    const careHomeId = searchParams.get('careHomeId');
    if (careHomeId && dependentId) {
      openConversation({ careHomeId }, dependentId).then(() => {
        setShowMobileChat(true);
      });
    }
  }, [searchParams, openConversation]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      setActiveConversation(id);
      setShowMobileChat(true);
    },
    [setActiveConversation]
  );

  const handleBack = useCallback(() => {
    setShowMobileChat(false);
    setActiveConversation(null);
  }, [setActiveConversation]);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);
  const conversationTitle = activeConversation
    ? activeConversation.type === 'CARE_HOME'
      ? `${activeConversation.careHome?.name || 'Care Home'} — General`
      : activeConversation.type === 'FAMILY'
      ? 'Family chat'
      : activeConversation.type === 'DIRECT'
      ? activeConversation.otherParticipant?.name || 'Direct message'
      : activeConversation.dependent?.name || 'Conversation'
    : '';

  if (!user) return null;

  return (
    <div className="flex h-full overflow-hidden bg-white">
      {/* Conversation list (hidden on mobile when chat is open) */}
      <div
        className={`w-full md:w-80 md:border-r border-gray-200 shrink-0 overflow-y-auto ${
          showMobileChat ? 'hidden md:block' : 'block'
        }`}
      >
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Messages</h2>
        </div>
        <ConversationList
          conversations={conversations}
          activeId={activeConversationId}
          onSelect={handleSelectConversation}
          loading={conversationsLoading}
        />
      </div>

      {/* Chat panel */}
      <div
        className={`flex-1 min-w-0 ${
          showMobileChat ? 'block' : 'hidden md:block'
        }`}
      >
        {activeConversationId ? (
          <ChatPanel
            conversationTitle={conversationTitle}
            messages={messages}
            currentUserId={user.id}
            loading={messagesLoading}
            hasMore={hasMoreMessages}
            onSend={sendMessage}
            onLoadMore={loadMoreMessages}
            onMarkRead={markAsRead}
            onBack={handleBack}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-2">
            <svg className="w-10 h-10 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
            </svg>
            <span className="text-sm">Select a conversation to start messaging</span>
          </div>
        )}
      </div>
    </div>
  );
}
